import {
  SENSOR_TOWER_TAG_TITLE_MAP,
  APPMAGIC_TAG_TITLE_MAP,
  RETENTION_D_RANGE_TITLE_MAP,
  RETENTION_D_RANGE_DEFS,
} from '../_config';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { feachTimeTypeToFilterTimeType } from '@/pages/intelligence/utils/common';
import type {
  IStickyHeaderInputItem,
  IStickySummaryItem,
} from './types';

/**
 * 吸顶筛选器展示文本映射表 - 用于把筛选器 key 映射到对应的 i18n label。
 */
const KEY_LABEL_I18N: Record<string, string> = {
  rangeTime: 'dbi_release_date',
  rangeReleaseTime: 'dbi_release_date',
  releaseTime: 'dbi_release_date',
  region: 'dbi_area_region',
  region_segement: 'dbi_area_region',
  region_country_selector: 'dbi_area_region',
  country: 'dbi_area_region',
  composed_country_select: 'dbi_country',
  multiple_area_select: 'dbi_area_region',
  single_area_select: 'dbi_area_region',
  platForm: 'dbi_platform',
  platform_cascader: 'dbi_platform',
  competitor_platform: 'dbi_platform',
  streaming_platForm: 'dbi_platform',
  single_platform_select: 'dbi_platform',
  console_device: 'dbi_platform',
  digital_device: 'dbi_platform',
  lifetime_device: 'dbi_platform',
  mau_device: 'dbi_platform',
  physical_device: 'dbi_platform',
  metrictable_device: 'dbi_platform',
  gsd_device: 'dbi_platform',
  dataSource: 'dbi_source',
  dataSourceSingle: 'dbi_source',
  publisher: 'dbi_publisher',
  publisher_company: 'dbi_publisher',
  publisher_class: 'dbi_publisher_class',
  publisher_region: 'dbi_publisher_region',
  developer: 'dbi_developer',
  genretor: 'dbi_top_genre',
  genretorWithSegment: 'dbi_top_genre',
  hotTagsSource: 'dbi_hot_tags',
  steam_tag: 'dbi_top_steam_tags',
  game_mode: 'dbi_top_game_mode',
  game_engine: 'dbi_top_game_engine',
  allComapys: 'dbi_company',
  wishlists_range: 'dbi_wishlists_range',
  allGames: 'dbi_games',
  revenueRange: 'dbi_revenue_range',
  revenueGrowth: 'dbi_revenue_growth',
  arpuRange: 'dbi_arpu_range',
  activeUsersRange: 'dbi_active_users_range',
  downloadsRange: 'dbi_downloads_range',
  rpdRange: 'dbi_rpd_range',
  ...RETENTION_D_RANGE_TITLE_MAP,
  ...SENSOR_TOWER_TAG_TITLE_MAP,
  ...APPMAGIC_TAG_TITLE_MAP,
};

/** 全选占位符常量，筛选器组件上报此值代表"全选"（由 buildStickySummaryItems 转为 i18n 的 "All"） */
export const SUMMARY_DISPLAY_ALL = '__ALL__';

/** 非筛选器的布局/功能 key，不应在吸顶摘要栏展示 */
const EXCLUDED_KEYS = new Set([
  'myFilter',
  'row',
  'left',
  'right',
  'summaryCard',
  'sourceExplanation',
  'dataSourceExplanationTrigger',
  'dataSourceExplanationBody',
  'saveFilterSettings',
  'reset',
  'KPITrends_reset',
  'KPITrends_calibration_metrics_switcher',
  'download',
  'TIPICON',
]);

/** 时间筛选器吸顶标题：按当前粒度展示（Day/Week/Month），替代固定的 "Release Date" */
const TIME_GRANULARITY_I18N: Record<string, string> = {
  date: 'dbi_day',
  week: 'dbi_week',
  month: 'dbi_month',
};

interface IntlDescriptor {
  id: string;
}

type FormatMessageFn = (descriptor: IntlDescriptor) => string;

export interface IBuildStickySummaryItemsParams {
  inputList: IStickyHeaderInputItem[];
  value: Record<string, any>;
  formatMessage: FormatMessageFn;
  locale: string;
  summaryDisplayMap?: Record<string, string>;
}

const tFromI18n = (
  formatMessage: FormatMessageFn,
  id?: string,
  defaultMessage?: string,
): string => {
  if (!id) return '';
  try {
    const v = formatMessage({ id, defaultMessage });
    return v === id ? (defaultMessage || '') : v;
  } catch {
    return defaultMessage || '';
  }
};

const RANGE_LABEL_DEFAULTS: Record<string, { 'zh-CN': string; 'en-US': string }> = {
  dbi_rpd_range: { 'zh-CN': 'RPD区间', 'en-US': 'RPD Range' },
  ...RETENTION_D_RANGE_DEFS.reduce((acc, item) => {
    acc[item.titleKey] = { 'zh-CN': item.labelZh, 'en-US': item.labelEn };
    return acc;
  }, {} as Record<string, { 'zh-CN': string; 'en-US': string }>),
};

const getRangeLabelDefault = (id: string, locale: string): string | undefined => {
  const fallback = RANGE_LABEL_DEFAULTS[id];
  if (!fallback) return undefined;
  return locale === 'zh-CN' ? fallback['zh-CN'] : fallback['en-US'];
};

const resolveLabel = (
  item: IStickyHeaderInputItem,
  formatMessage: FormatMessageFn,
  locale: string,
  rawValue?: any,
): string => {
  const key = item?.key as string;
  // 时间筛选器：标题展示当前粒度（Day/Week/Month），而非固定的 "Release Date"
  if (key === 'rangeTime') {
    const gid = TIME_GRANULARITY_I18N[feachTimeTypeToFilterTimeType(rawValue?.type)];
    if (gid) return tFromI18n(formatMessage, gid);
  }
  const candidate =
    KEY_LABEL_I18N[key] ||
    item?.title ||
    item?.btnName ||
    item?.buttonName ||
    item?.text ||
    item?.placeholder;
  if (typeof candidate === 'string' && candidate.startsWith('dbi_')) {
    const defaultMessage = getRangeLabelDefault(candidate, locale);
    const v = tFromI18n(formatMessage, candidate, defaultMessage);
    if (v) return v;
  }
  if (typeof candidate === 'string' && candidate) return candidate;
  return key || '';
};

const getStickySourceIcons = (key: string, raw: any): string[] => {
  if (key !== 'dataSource' && key !== 'dataSourceSingle') return [];
  const values = Array.isArray(raw?.value)
    ? raw.value.filter(Boolean)
    : [raw?.value].filter(Boolean);
  if (values.length !== 1) return [];
  const icon = getDataSourceIcon(values[0]);
  return icon ? [icon] : [];
};

const getLabelIcon = (key: string): IStickySummaryItem['labelIcon'] => {
  if (key !== 'hotTagsSource') return undefined;
  return {
    type: 'new-logo',
    className: 'dbi-hot-tags-source-icon',
  };
};

/**
 * 吸顶摘要栏数据构建器 —— 纯数据驱动，唯一数据源是 summaryDisplayMap。
 *
 * 规则：
 * - summaryDisplayMap[key] === '__ALL__'  → 展示 i18n "All"
 * - summaryDisplayMap[key] === ''         → 展示 i18n "None"
 * - summaryDisplayMap[key] === undefined  → 展示 i18n "None"（组件尚未上报）
 * - 其他值                                → 原样展示（组件上报的按钮文本）
 */
export const buildStickySummaryItems = ({
  inputList = [],
  value,
  formatMessage,
  locale = 'en-US',
  summaryDisplayMap = {},
}: IBuildStickySummaryItemsParams): IStickySummaryItem[] => {
  if (!value) return [];

  const allText = tFromI18n(formatMessage, 'dbi_all') || 'All';
  const noneText = tFromI18n(formatMessage, 'dbi_filterSave_none') || 'None';

  return inputList
    .map(item => {
      const key = item?.key;
      if (!key) return null;
      if (item?.visible === 0 || item?.visible === 2) return null;
      if (EXCLUDED_KEYS.has(key)) return null;

      const reportedDisplay = summaryDisplayMap[key];

      let display: string;
      if (reportedDisplay === SUMMARY_DISPLAY_ALL) {
        display = allText;
      } else if (!reportedDisplay) {
        display = noneText;
      } else {
        display = reportedDisplay;
      }

      return {
        key,
        title: resolveLabel(item, formatMessage, locale, value[key]),
        display,
        valueIconUrls: getStickySourceIcons(key, value[key]),
        labelIcon: getLabelIcon(key),
      };
    })
    .filter(Boolean) as IStickySummaryItem[];
};
